import React, { useState, useContext, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import 'react-day-picker/dist/style.css';
import { AuthContext } from '../contexts/AuthContext';
import { 
  bookPublicItinerary, 
  getUserTrips
} from '../services/api';
import cacheService from '../services/cacheService';
import AuthModal from './AuthModal';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import POIDetails from './POIDetails';
import L from 'leaflet';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Toast from './Toast';
import TripCreationModal from './TripCreationModal';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow: hidden;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 12px;
  width: 70%;
  height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  overflow: hidden;
  position: relative;
  max-width: 1400px;

  @media (max-width: 768px) {
    width: 95%;
    height: 90vh;
    margin: 10px;
  }
`;

const Title = styled.h2`
  color: #0A2429;
  text-align: left;
  margin: 0;
  font: normal normal bold 30px/40px Poppins;
  letter-spacing: 0px;
  font-style: normal;

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const ImageSection = styled.div`
  flex: ${props => props.isModal ? '1' : 'none'};
  position: relative;
  overflow: hidden;
  min-height: 300px;

  @media (max-width: 768px) {
    flex: none;
    height: 30vh;
    min-height: 200px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.currentIndex * 100}%);
`;

const Image = styled.div`
  flex: 0 0 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const ImageNavigationButtons = styled.div`
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 2;
`;

const ImageNavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  cursor: pointer;
  z-index: 2;
  ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  opacity: 0.7;
  font-size: 1.2em;

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const SectionCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin: 15px 0;
  padding: 15px;
`;

const TripOverviewSection = styled(SectionCard)`
  .leaflet-container {
    height: 300px;
    border-radius: 8px;
    z-index: 1;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: ${props => props.isModal ? 'row' : 'column'};
  height: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoSection = styled.div`
  flex: ${props => props.isModal ? '1' : 'none'};
  padding: 20px;
  overflow-y: auto;
  position: relative;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 15px;
    padding-bottom: 80px; /* Add padding to prevent content from being hidden behind the fixed button */
  }
`;

const Description = styled.p`
  color: #0A2429;
  text-align: left;
  font: normal normal medium 16px/25px Poppins;
  letter-spacing: 0px;
  margin: 10px 0;
  white-space: pre-line;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  padding: 0 20px;
  position: relative;
  background: white;

  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    padding: 15px 20px;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1010;
  }
`;

const AddToTripButton = styled.button`
  padding: 10px 20px;
  background-color: #F38530;
  color: #FFFFFF;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 80%;
  text-align: center;
  text-transform: uppercase;
  font: normal normal 600 16px/25px Poppins;
  letter-spacing: 0px;
  
  &:hover {
    background-color: #d67428;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;

  h3 {
    margin: 0;
    color: #316C6E;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: auto;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  z-index: 1011;
  max-height: 300px;
  overflow-y: auto;
  min-width: 300px;

  @media (max-width: 768px) {
    width: calc(100% - 40px);
    min-width: unset;
  }
`;

const DropdownItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  cursor: pointer;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  span.trip-name {
    font-weight: 500;
  }
  
  span.trip-dates {
    font-size: 12px;
    color: #666;
  }
`;

const NewTripOption = styled(DropdownItem)`
  background-color: #f8f8f8;
  border-bottom: 2px solid #e0e0e0;
  color: #316C6E;
  font-weight: 500;
  padding: 12px 15px;
  
  &:hover {
    background-color: #eef8f8;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1002;
  font-size: 20px;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
  }
`;

const getFirstImage = (poi) => {
  if (!poi.image_url) return '';
  try {
    const images = JSON.parse(poi.image_url);
    return Array.isArray(images) ? images[0] : poi.image_url;
  } catch {
    return poi.image_url;
  }
};

const createCustomIcon = (poi) => {
  const element = document.createElement('div');
  element.className = 'custom-overlay home-screen';
  
  const imgElement = document.createElement('div');
  imgElement.style.backgroundImage = `url(${getFirstImage(poi)})`;
  element.appendChild(imgElement);

  return L.divIcon({
    className: 'custom-icon',
    html: element,
    iconSize: [78, 78],
    iconAnchor: [39, 78],
  });
};

const PublicItineraryModal = ({ itinerary, onClose, open }) => {
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const { isAuthenticated } = useContext(AuthContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [error, setError] = useState('');
  const [selectedPOI, setSelectedPOI] = useState(null);
  const history = useHistory();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [toastLink, setToastLink] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [autoMove, setAutoMove] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userTrips, setUserTrips] = useState([]);
  const [showTripCreationModal, setShowTripCreationModal] = useState(false);
  const dropdownRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language].pages.publicItinerary;

  useEffect(() => {
    let timer;
    if (open && autoMove && itinerary?.pois && itinerary.pois.length > 1) {
      const images = itinerary.pois.map(poi => {
        try {
          const images = JSON.parse(poi.image_url);
          return Array.isArray(images) ? images[0] : poi.image_url;
        } catch {
          return poi.image_url;
        }
      }).filter(Boolean);

      if (images.length > 1) {
        timer = setInterval(() => {
          setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, 5000);
      }
    }
    return () => clearInterval(timer);
  }, [autoMove, itinerary, open]);

  useEffect(() => {
    if (open && isAuthenticated) {
      const fetchUserTrips = async () => {
        try {
          const trips = await getUserTrips();
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const activeTrips = trips
            .filter(trip => new Date(trip.end_date) >= today)
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
          
          setUserTrips(activeTrips);
        } catch (error) {
          console.error('Error fetching user trips:', error);
          setUserTrips([]);
        }
      };

      fetchUserTrips();
    }
  }, [isAuthenticated, open]);

  useEffect(() => {
    if (open) {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setShowDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open]);

  if (!open || !itinerary || !itinerary.pois) {
    return null;
  }

  const allImages = itinerary.pois.map(poi => {
    try {
      const images = JSON.parse(poi.image_url);
      return Array.isArray(images) ? images[0] : poi.image_url;
    } catch {
      return poi.image_url;
    }
  }).filter(Boolean);

  const handleStartDateChange = (e) => {
    const newFrom = new Date(e.target.value);
    setDateRange(prev => ({
      ...prev,
      from: newFrom
    }));
    validateDateRange(newFrom, dateRange.to);
  };

  const handleEndDateChange = (e) => {
    const newTo = new Date(e.target.value);
    setDateRange(prev => ({
      ...prev,
      to: newTo
    }));
    validateDateRange(dateRange.from, newTo);
  };

  const validateDateRange = (from, to) => {
    if (from && to) {
      const daysDifference = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
      if (daysDifference < itinerary.duration) {
        setError(`Please select at least ${itinerary.duration} days for this itinerary.`);
      } else {
        setError('');
      }
    }
  };

  const handleAddToTrip = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (userTrips.length === 0) {
      setShowTripCreationModal(true);
    } else {
      setShowDropdown(true);
    }
  };

  const handleClosePOIDetails = () => {
    setSelectedPOI(null);
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    
    try {
      const trips = await getUserTrips();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const activeTrips = trips
        .filter(trip => new Date(trip.end_date) >= today)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      
      setUserTrips(activeTrips);
      
      if (activeTrips.length === 0) {
        setShowTripCreationModal(true);
      } else {
        setShowDropdown(true);
      }
    } catch (error) {
      console.error('Error fetching user trips:', error);
      setUserTrips([]);
      setShowTripCreationModal(true);
    }
  };

  const isDateRangeValid = dateRange.from && dateRange.to && !error;

  const calculateBounds = () => {
    if (!itinerary.pois || itinerary.pois.length === 0) {
      return L.latLngBounds(
        [17.5, -72],
        [20, -68]
      );
    }

    const validPOIs = itinerary.pois.filter(poi => {
      const lat = Number(poi.lat);
      const lng = Number(poi.lng);
      return !isNaN(lat) && !isNaN(lng) && 
             lat >= -90 && lat <= 90 && 
             lng >= -180 && lng <= 180;
    });

    if (validPOIs.length === 0) {
      return L.latLngBounds(
        [17.5, -72],
        [20, -68]
      );
    }

    const bounds = L.latLngBounds(
      validPOIs.map(poi => [Number(poi.lat), Number(poi.lng)])
    );
    
    return bounds.pad(0.2);
  };

  const renderMarkers = () => {
    return itinerary.pois.map((poi) => {
      const lat = Number(poi.lat);
      const lng = Number(poi.lng);
      
      if (isNaN(lat) || isNaN(lng) || 
          lat < -90 || lat > 90 || 
          lng < -180 || lng > 180) {
        console.warn(`Invalid coordinates for POI ${poi.id}: lat=${poi.lat}, lng=${poi.lng}`);
        return null;
      }

      return (
        <Marker
          key={poi.id}
          position={[lat, lng]}
          icon={createCustomIcon(poi)}
        />
      );
    }).filter(marker => marker !== null);
  };

  const handlePrevImage = () => {
    setAutoMove(false);
    setCurrentImageIndex((prevIndex) => 
      (prevIndex - 1 + allImages.length) % allImages.length
    );
  };

  const handleNextImage = () => {
    setAutoMove(false);
    setCurrentImageIndex((prevIndex) => 
      (prevIndex + 1) % allImages.length
    );
  };

  const handleTripSelect = async (tripId) => {
    try {
      let addedCount = 0;
      let existingCount = 0;

      for (const poi of itinerary.pois) {
        try {
          await cacheService.addPOIToTrip(tripId, poi.id);
          addedCount++;
        } catch (error) {
          if (error.response?.status === 409) {
            existingCount++;
          } else {
            throw error;
          }
        }
      }

      setShowDropdown(false);

      let message;
      if (existingCount === 0) {
        message = 'All POIs added to trip successfully!';
      } else if (addedCount === 0) {
        message = 'All POIs were already in your trip!';
      } else {
        message = `Added ${addedCount} new POI${addedCount !== 1 ? 's' : ''} to your trip! (${existingCount} already existed)`;
      }

      showToastMessage(message, 'success', {
        url: `/trip/${tripId}`,
        text: 'View Trip'
      });
    } catch (error) {
      showToastMessage('Error adding POIs to trip. Please try again.', 'error');
    }
  };

  const handleCreateTrip = async (tripData) => {
    try {
      const newTrip = await cacheService.createTrip({
        name: itinerary.name,
        ...tripData,
        pois: itinerary.pois.map(poi => poi.id)
      });
      
      setShowTripCreationModal(false);
      
      showToastMessage(
        'Trip created successfully!', 
        'success', 
        {
          url: `/trip/${newTrip.id}`,
          text: 'View Trip'
        }
      );

      setTimeout(() => {
        history.push(`/trip/${newTrip.id}`);
      }, 2000);
    } catch (error) {
      console.error('Error creating trip:', error);
      showToastMessage('Failed to create trip. Please try again.', 'error');
    }
  };

  const showToastMessage = (message, type = 'success', link = null) => {
    setToastMessage(message);
    setToastType(type);
    setToastLink(link);
    setShowToast(true);
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        <Content isModal={true}>
          <ImageSection isModal={true}>
            <ImageWrapper currentIndex={currentImageIndex}>
              {allImages.map((image, index) => (
                <Image key={index} image={image} />
              ))}
            </ImageWrapper>
            {allImages.length > 1 && (
              <>
                <ImageNavigationButton 
                  direction="left" 
                  onClick={handlePrevImage}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </ImageNavigationButton>
                <ImageNavigationButton 
                  direction="right" 
                  onClick={handleNextImage}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </ImageNavigationButton>
              </>
            )}
          </ImageSection>

          <InfoSection isModal={true}>
            <Title>
              {language === 'es' && itinerary.name_es ? itinerary.name_es : itinerary.name}
            </Title>
            <Description>
              {language === 'es' && itinerary.description_es ? itinerary.description_es : itinerary.description}
            </Description>

            <ButtonsContainer>
              <AddToTripButton onClick={handleAddToTrip}>
                {t.scheduleTrip}
              </AddToTripButton>
              {showDropdown && (
                <DropdownMenu ref={dropdownRef}>
                  <NewTripOption onClick={() => setShowTripCreationModal(true)}>
                    {t.createNewTrip}
                  </NewTripOption>
                  {userTrips.map(trip => (
                    <DropdownItem 
                      key={trip.id} 
                      onClick={() => handleTripSelect(trip.id)}
                    >
                      <span className="trip-name">{trip.name}</span>
                      <span className="trip-dates">
                        {format(new Date(trip.start_date), 'MMM d')} - {format(new Date(trip.end_date), 'MMM d, yyyy')}
                      </span>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              )}
            </ButtonsContainer>

            <TripOverviewSection>
              <SectionHeader>
                <h3>{t.tripOverview}</h3>
              </SectionHeader>
              <MapContainer
                bounds={calculateBounds()}
                style={{ width: '100%' }}
                minZoom={7}
                maxBounds={[
                  [17.5, -72],
                  [20, -68]
                ]}
                zoomControl={false}
              >
                <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
                {renderMarkers()}
              </MapContainer>
            </TripOverviewSection>
          </InfoSection>
        </Content>
      </ModalContent>

      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      )}
      
      {selectedPOI && (
        <POIDetails
          poi={selectedPOI}
          onClose={handleClosePOIDetails}
          isModal={true}
        />
      )}

      {showToast && (
        <Toast 
          message={toastMessage}
          onClose={() => setShowToast(false)}
          type={toastType}
          link={toastLink}
        />
      )}

      {showTripCreationModal && (
        <TripCreationModal
          onClose={(e) => {
            e?.stopPropagation?.();
            setShowTripCreationModal(false);
          }}
          onCreateTrip={handleCreateTrip}
          initialName={itinerary.name}
          recommendedDuration={itinerary.duration}
          onDateChange={(dates) => {
            if (dates.startDate && dates.endDate) {
              const daysDiff = Math.ceil(
                (new Date(dates.endDate) - new Date(dates.startDate)) / (1000 * 60 * 60 * 24)
              ) + 1;
              
              return daysDiff < itinerary.duration ? {
                show: true,
                message: `We recommend at least ${itinerary.duration} days for the best experience of this trip.`
              } : {
                show: false,
                message: ''
              };
            }
            return { show: false, message: '' };
          }}
        />
      )}
    </ModalOverlay>
  );
};

export default PublicItineraryModal;
